import { create } from "zustand";

type UserRole = "consumer" | "provider";
type InboxFilter = "ACTIVE" | "URGENT" | "MANUAL";

interface AppState {
  // Navigation
  currentView: string;
  viewHistory: string[];
  selectedProviderId: string | null;
  seeAllMode: "top" | "recommended" | null;
  selectedCategory: string | null;
  userRole: UserRole;
  roleLoaded: boolean;
  profileName: string | null;
  inboxFilter: InboxFilter;
  activeProviderChat: any | null; // Using any for now to avoid circular dependency or complex type imports, but strictly it's ConversationSummary

  // AI Credits
  aiCreditsBalance: number | null;
  businessId: string | null;

  // Navigation actions
  navigateTo: (view: string) => void;
  goBack: (fallback?: string) => void;
  handleTabNavigate: (view: string) => void;
  setCurrentView: (view: string) => void;

  // State setters
  setSelectedProviderId: (id: string | null) => void;
  setSeeAllMode: (mode: "top" | "recommended" | null) => void;
  setSelectedCategory: (cat: string | null) => void;
  setUserRole: (role: UserRole) => void;
  setRoleLoaded: (loaded: boolean) => void;
  setProfileName: (name: string | null) => void;
  setInboxFilter: (filter: InboxFilter) => void;
  setAiCreditsBalance: (balance: number | null) => void;
  setBusinessId: (id: string | null) => void;
  setActiveProviderChat: (chat: any | null) => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  currentView: "home",
  viewHistory: [],
  selectedProviderId: null,
  seeAllMode: null,
  selectedCategory: null,
  userRole: "consumer",
  roleLoaded: false,
  profileName: null,
  inboxFilter: "ACTIVE",
  aiCreditsBalance: null,
  businessId: null,
  activeProviderChat: null,

  navigateTo: (view: string) => {
    const { currentView } = get();
    set((state) => ({
      viewHistory: [...state.viewHistory, currentView],
      currentView: view,
    }));
  },

  goBack: (fallback = "home") => {
    set((state) => {
      const next = [...state.viewHistory];
      const last = next.pop();
      return {
        viewHistory: next,
        currentView: last || fallback,
      };
    });
  },

  handleTabNavigate: (view: string) => {
    set({ viewHistory: [], currentView: view });
  },

  setCurrentView: (view: string) => set({ currentView: view }),
  setSelectedProviderId: (id) => set({ selectedProviderId: id }),
  setSeeAllMode: (mode) => set({ seeAllMode: mode }),
  setSelectedCategory: (cat) => set({ selectedCategory: cat }),
  setUserRole: (role) => set({ userRole: role }),
  setRoleLoaded: (loaded) => set({ roleLoaded: loaded }),
  setProfileName: (name) => set({ profileName: name }),
  setInboxFilter: (filter) => set({ inboxFilter: filter }),
  setAiCreditsBalance: (balance) => set({ aiCreditsBalance: balance }),
  setBusinessId: (id) => set({ businessId: id }),
  setActiveProviderChat: (chat) => set({ activeProviderChat: chat }),
}));
